<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardFieldOption, WizardFieldOptionPayload } from '@/types/wizard-forms'

const props = defineProps<{
  options: WizardFieldOption[]
}>()

const emit = defineEmits<{
  update: [options: WizardFieldOptionPayload[]]
}>()

const { t } = useI18n()

interface LocalOption {
  id?: number
  label: string
  value: string
  extra_cost: string
  sort_order: number
}

const localOptions = ref<LocalOption[]>([])

function initFromProps(): void {
  localOptions.value = props.options.map((o) => ({
    id: o.id,
    label: o.label,
    value: o.value,
    extra_cost: o.extra_cost,
    sort_order: o.sort_order,
  }))
}

watch(() => props.options, initFromProps, { immediate: true })

function deriveValue(label: string): string {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
  return slug || 'option'
}

function addOption(): void {
  localOptions.value.push({
    label: '',
    value: '',
    extra_cost: '0.00',
    sort_order: localOptions.value.length,
  })
  emitUpdate()
}

function removeOption(index: number): void {
  localOptions.value.splice(index, 1)
  emitUpdate()
}

function emitUpdate(): void {
  emit(
    'update',
    localOptions.value.map((o, i) => ({
      ...(o.id !== undefined ? { id: o.id } : {}),
      label: o.label,
      value: deriveValue(o.label),
      extra_cost: parseFloat(o.extra_cost) || 0,
      sort_order: i,
    })),
  )
}

const dragSourceIdx = ref<number | null>(null)
const dragOverIdx = ref<number | null>(null)

function onDragStart(idx: number): void {
  dragSourceIdx.value = idx
}

function onDragOver(event: DragEvent, idx: number): void {
  event.preventDefault()
  dragOverIdx.value = idx
}

function onDrop(targetIdx: number): void {
  if (dragSourceIdx.value === null || dragSourceIdx.value === targetIdx) {
    dragSourceIdx.value = null
    dragOverIdx.value = null
    return
  }
  const arr = [...localOptions.value]
  const [moved] = arr.splice(dragSourceIdx.value, 1)
  arr.splice(targetIdx, 0, moved)
  localOptions.value = arr
  dragSourceIdx.value = null
  dragOverIdx.value = null
  emitUpdate()
}

function onDragEnd(): void {
  dragSourceIdx.value = null
  dragOverIdx.value = null
}
</script>

<template>
  <div class="space-y-2">
    <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
      {{ t('wizard_forms.options.heading') }}
    </p>

    <div
      v-for="(opt, idx) in localOptions"
      :key="idx"
      draggable="true"
      class="flex items-center gap-2 rounded-lg bg-slate-700/30 border border-slate-600 px-3 py-2 transition-colors"
      :class="dragOverIdx === idx && dragSourceIdx !== idx ? 'border-emerald-400' : ''"
      @dragstart="onDragStart(idx)"
      @dragover="onDragOver($event, idx)"
      @drop="onDrop(idx)"
      @dragend="onDragEnd"
    >
      <svg class="w-3.5 h-3.5 text-slate-500 cursor-grab shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      <input
        v-model="opt.label"
        type="text"
        :placeholder="t('wizard_forms.options.label_placeholder')"
        class="flex-1 min-w-0 rounded bg-slate-700 border border-slate-600 px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        @input="emitUpdate"
      />
      <div class="flex items-center gap-1 shrink-0">
        <span class="text-xs text-slate-400">€</span>
        <input
          v-model="opt.extra_cost"
          type="number"
          min="0"
          step="0.01"
          :placeholder="'0.00'"
          class="w-16 rounded bg-slate-700 border border-slate-600 px-2 py-1 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          @input="emitUpdate"
        />
      </div>
      <button
        type="button"
        class="shrink-0 text-red-400 hover:text-red-300 p-0.5 rounded transition-colors"
        :title="t('common.delete')"
        @click="removeOption(idx)"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <p v-if="localOptions.length === 0" class="text-xs text-slate-500 italic">
      {{ t('wizard_forms.options.empty') }}
    </p>

    <button
      type="button"
      class="flex items-center gap-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors"
      @click="addOption"
    >
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      {{ t('wizard_forms.options.add_option') }}
    </button>
  </div>
</template>
